import { useEffect } from 'react';
import { useConnectionData, useConnectionContext } from '../../contexts/ConnectionStateContext';
import { saveContactToPhone } from '../../utils/contactUtils';
import { resolveConnectionNote } from '../../utils/connectionNote';
import { getConnectionMetadata } from '../../services/storage/userDataStorage';
import { logFunnelContactSaved } from '../../services/analytics/eventLogger';

/**
 * ConnectionDetailPage - Base component for detailed connection view
 *
 * This component serves as the foundation for both anon and profile user
 * detail views. It handles data fetching, note editing, and the "save to
 * contacts" vCard action, then hands the resolved data to a children-as-function
 * render prop. Both consumers (ProfileConnectionDetailPage, AnonConnectionDetailPage)
 * render their own layout and own their own back navigation — this component
 * renders no layout of its own.
 */
const ConnectionDetailPage = ({ chatRoomId: propsChatRoomId, isProfileUser, children }) => {
  // chatRoomId must come from props (navigation params) - no fallbacks
  const chatRoomId = propsChatRoomId;

  // Get connection data from context - NO FALLBACKS
  const connectionData = useConnectionData(chatRoomId);
  const { loadConnectionData, updateContactSavedStatus } = useConnectionContext();

  // Destructure data directly from context
  // Profile user views anonData, Anon user views profileData
  const rawUserData = isProfileUser ? connectionData?.anonData : connectionData?.profileData;

  // Use data directly - sharedFields comes from the message
  const userData = rawUserData;

  const connectionMetadata = connectionData?.connectionMetadata;

  // Load connection data when component mounts
  useEffect(() => {
    if (chatRoomId) {
      console.log(
        '[CONNECTION_DETAIL] Loading connection data from context for:',
        chatRoomId,
        'isProfileUser:',
        isProfileUser
      );
      loadConnectionData(chatRoomId, isProfileUser);
    }
  }, [chatRoomId, isProfileUser, loadConnectionData]);

  // The note render-props (note / persistedNote / handleNoteChange /
  // handleNoteSave / saveNote) were DELETED 2026-08-20. Note and Nickname
  // merged into one field owned by `useConnectionNote`, and all three detail
  // pages moved to it — leaving this seeded from the legacy `notes` column with
  // no consumer. It was worse than unused: `saveNote` wrote that pre-merge text
  // back, so the next page to pick up the documented render-prop would have
  // overwritten a connection the migration had just drained.

  // Helper function for children-as-function pattern
  // Go straight to vCard download (no note overlay)
  const onAddToContacts = async () => {
    console.log('[ConnectionDetailPage] onAddToContacts called - saving contact directly');

    if (!userData) return;

    // Analytics (Anon side only) — must complete before vCard download
    if (!isProfileUser) {
      await logFunnelContactSaved(chatRoomId, 'detail_page');
    }

    // Get fresh connection metadata
    const freshConnectionMetadata = await getConnectionMetadata(chatRoomId);

    // Use shared saveContactToPhone function — nickname rides into the vCard
    // (Company rule); the app note stays in the app. The vCard mirrors the
    // page AS VIEWED at save time: the viewer axis drives all gating inside
    // saveContactToPhone (a re-save after converting picks up the fuller
    // Profile view automatically).
    await saveContactToPhone({
      userData,
      chatRoomId,
      connectionMetadata: freshConnectionMetadata,
      // Resolved, not raw: the drain-into-nickname migration is triggered by a
      // detail page mounting, and Save-to-contacts can run before it lands. The
      // raw read put a legacy note-only connection on the phone with no note.
      nickname: resolveConnectionNote(connectionData).text,
      viewerIsProfile: isProfileUser,
      updateContactSavedStatus,
    });
  };

  // Children-as-function pattern — both consumers render their own layout.
  return children({
    userData,
    connectionMetadata,
    onAddToContacts,
    chatMessages: connectionData?.chatMessages || [],
  });
};

export default ConnectionDetailPage;
